<template>
  <div class="topbar">
    <div class="document-name-container">
      <BbIcon size="medium" name="pdf" />
      <BbBtn size="large" type="tertiary">{{ title }}</BbBtn>
    </div>

    <language-toggle />

    <div class="document-actions">
      <BbBtn
        v-if="isNotNovoOrg() && !!currentLanguageCode && pdfSrc"
        size="small"
        type="secondary"
        @click="downloadDocumentPdf"
      >
        <BbIcon size="small" name="download" />
        <TextBody i18n-key="common.download" tag="span" />
      </BbBtn>
      <BbBtn
        v-if="isNotNovoOrg() && !!currentLanguageCode && hasSourceAvailable"
        size="small"
        type="secondary"
        @click="downloadDocumentSourceTranslation"
      >
        <BbIcon size="small" name="download" />
        <TextBody i18n-key="common.downloadSource" tag="span" />
      </BbBtn>
      <BbBtn size="small" type="secondary" @click="translateDocument" v-if="selectedReference">
        <BbIcon size="small" name="translate" />
        <TextBody i18n-key="common.translate" tag="span" />
      </BbBtn>
      <BbBtn size="small" type="secondary" @click="openInSource" v-if="displayVeevaButton">
        <BbIcon size="small" name="veeva" />
        <TextBody i18n-key="common.open" tag="span" />
      </BbBtn>
      <BbBtn size="x-small" type="icon" icon="x-small" icon-size="medium" no-background @click="onPreviewClose"></BbBtn>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue';
import useDocuments from '~/composables/useDocuments';
import useModals from '~/composables/useModals';
import useTranslations from '~/composables/useTranslation';
import useOrgConfig from '~/composables/useOrgConfig';
import LanguageToggle from './LanguageToggle.vue';
import TextBody from '~/components/common/TextBody';

export default {
  components: { TextBody, LanguageToggle },
  props: {
    externalId: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    sourceUrl: {
      type: String,
      default: '',
    },
    pdfSrc: {
      type: String,
      default: '',
    },
    displayVeevaButton: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const { getSourceUrl, getSourceTranslationAvailable, selectedReference } = useDocuments();
    const { showTranslationModal, hidePreviewDocument } = useModals();
    const { currentLanguageCode, setCurrentPdfLanguage } = useTranslations();
    const { isNotNovoOrg } = useOrgConfig();

    const hasSourceAvailable = ref(false);

    const translateDocument = () => {
      if (selectedReference.value) {
        const { externalId, name } = selectedReference.value;
        showTranslationModal(externalId, name);
      }
    };

    const openInSource = () => {
      window.open(props.sourceUrl || '');
    };

    const onPreviewClose = () => {
      hidePreviewDocument();
      // reset currentLanguageCode when pdf viewer is closed
      setCurrentPdfLanguage('');
    };

    const downloadDocumentPdf = async () => {
      window.open(props.pdfSrc);
    };

    const downloadDocumentSourceTranslation = async () => {
      const url = await getSourceUrl(props.externalId, currentLanguageCode.value);
      window.open(url);
    };

    onMounted(async () => {
      hasSourceAvailable.value = await getSourceTranslationAvailable(props.externalId);
    });

    return {
      currentLanguageCode,
      downloadDocumentPdf,
      downloadDocumentSourceTranslation,
      isNotNovoOrg,
      hasSourceAvailable,
      onPreviewClose,
      openInSource,
      translateDocument,
      selectedReference,
    };
  },
};
</script>

<style scoped lang="scss">
.topbar {
  padding: $bb-spacing-3;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: $bb-neutral-0;
  border-bottom: 1px solid $bb-neutral-4;

  .document-name-container {
    display: flex;
    align-items: center;
    gap: $bb-spacing-3;
  }

  .document-actions {
    display: flex;
    align-items: center;
    gap: $bb-spacing-2;

    :deep(.v-btn__content) {
      display: flex;
      align-items: center;
      gap: $bb-spacing-1;
    }
  }
}
</style>
